'use client';

import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextAreaField } from '@/components/atoms/text-area';
import { SelectField } from '@/components/atoms/select-field';
import { motion } from 'framer-motion';
import { Button } from '@/components/atoms/button';
import clsx from 'clsx';
import { getRoundedClassName } from '@/components/utils';

import { TextField } from '@/components/atoms/text-field'; // Your text input
import {
  DataAccessRequestFormSchema,
  DataAccessRequestFormSchemaType,
} from '@/data/data-access-request-dto';
import { createDataAccessRequest } from '@/data/data-access-request';
import { useQueryState } from 'nuqs';
import Link from 'next/link';
import { PageCollectionConfigRadiusClassName } from '@creator-suite/page-types';

export interface DataAccessRequestFormProps {
  language: 'de' | 'en';
  accountDisplayName: string;
  pageCollectionSlug: string;
  buttonRadiusClassName: PageCollectionConfigRadiusClassName;
  inputRadiusClassName: PageCollectionConfigRadiusClassName;
  cardRadiusClassName: PageCollectionConfigRadiusClassName;
}

export const DataAccessRequestForm: React.FC<DataAccessRequestFormProps> = (
  props,
) => {
  const {
    language,
    buttonRadiusClassName,
    inputRadiusClassName,
    accountDisplayName,
    pageCollectionSlug,
  } = props;
  const translations = {
    en: {
      requestTypeLabel: 'Request Type',
      requestData: 'Request Data',
      requestForDataDeletion: 'Request for Data Deletion',
      requestForDataCorrection: 'Request for Data Correction',
      other: 'Other',
      phoneNumberPlaceholder: 'Phone Number',
      emailPlaceholder: 'Email Address',
      commentsPlaceholder: 'Extra Comments',
      submitBtnText: 'Submit',
    },
    de: {
      requestTypeLabel: 'Anfragetyp',
      requestData: 'Daten anfordern',
      requestForDataDeletion: 'Löschung von Daten beantragen',
      requestForDataCorrection: 'Berichtigung von Daten beantragen',
      other: 'Andere',
      phoneNumberPlaceholder: 'Telefonnummer',
      emailPlaceholder: 'E-Mail',
      commentsPlaceholder: 'Zusätzliche Kommentare',
      submitBtnText: 'Absenden',
    },
  };

  const [state, setState] = useQueryState('state');

  const t = translations[language];

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DataAccessRequestFormSchemaType>({
    resolver: zodResolver(DataAccessRequestFormSchema),
  });

  const onSubmit = async (data: DataAccessRequestFormSchemaType) => {
    try {
      await createDataAccessRequest(
        pageCollectionSlug,
        accountDisplayName,
        data,
      );
      setState('success');
    } catch (error: unknown) {
      console.error(error);
      setState('error');
      return;
    }
  };

  const variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.5,
        stiffness: 100,
      },
    },
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={variants}>
      {state !== 'success' ? (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 overflow-hidden rounded-2xl border border-white/20 bg-[#787878]/20 px-4 py-8 backdrop-blur-lg md:px-8"
        >
          <Controller
            name="requestType"
            control={control}
            defaultValue="REQUEST_DATA"
            render={({ field }) => (
              <SelectField
                {...field}
                roundedClassName={inputRadiusClassName}
                label={t.requestTypeLabel}
                error={errors.requestType?.message}
                onChange={(val) => field.onChange(val)}
                options={[
                  { value: 'REQUEST_DATA', label: t.requestData },
                  {
                    value: 'REQUEST_DATA_DELETION',
                    label: t.requestForDataDeletion,
                  },
                  {
                    value: 'REQUEST_DATA_CORRECTION',
                    label: t.requestForDataCorrection,
                  },
                  { value: 'OTHER', label: t.other },
                ]}
              />
            )}
          />

          <Controller
            name="phoneNumber"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                placeholder={t.phoneNumberPlaceholder}
                roundedClassName={inputRadiusClassName}
                error={errors.phoneNumber?.message}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                placeholder={t.emailPlaceholder}
                roundedClassName={inputRadiusClassName}
                error={errors.email?.message}
              />
            )}
          />

          <Controller
            name="comments"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextAreaField
                {...field}
                roundedClassName={inputRadiusClassName}
                placeholder={t.commentsPlaceholder}
                error={errors.comments?.message}
                rows={4}
              />
            )}
          />

          <div className="flex justify-center">
            <Button
              type="submit"
              className={clsx(getRoundedClassName(buttonRadiusClassName))}
            >
              <span>{t.submitBtnText}</span>
            </Button>
          </div>
          {state === 'error' ? (
            <div>
              <p className="text-center text-red-500">
                {language === 'de'
                  ? 'Leider ist ein Fehler aufgetreten, bitte versuche es erneut.'
                  : 'Sorry, an error occurred, please try again.'}
              </p>
            </div>
          ) : null}
        </form>
      ) : (
        <div className="space-y-4 overflow-hidden rounded-2xl border border-white/20 bg-[#787878]/20 px-4 py-8 backdrop-blur-lg md:px-8">
          <p className="text-center">
            {language === 'de'
              ? 'Deine Anfrage wurde erfolgreich eingereicht.'
              : 'Your request has been successfully submitted.'}
          </p>
          <div className="flex justify-center">
            <Link href="/">
              {language === 'de' ? 'Zurück zur Startseite' : 'Back to Homepage'}
            </Link>
          </div>
        </div>
      )}
    </motion.div>
  );
};
